import * as React from 'react'
import map from 'lodash/map'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faPencilAlt, faList, faFileCsv} from '@fortawesome/free-solid-svg-icons'
import {exhaustive} from '@freckle/exhaustive'
import {getCountryByIso3} from '@frontrow/console/src/entities/helpers/countries'
import {mmap} from '@freckle/maybe'
import {toggleMembership} from '@frontrow/console/src/entities/helpers/common-helper'
import {Link} from '@frontrow/console/src/components/ui/link'
import {mkNonEmptyFromJust} from '@freckle/non-empty'
import ErrorAlert from '@frontrow/console/src/components/common/error-alert'
import LabelledInput from '@frontrow/console/src/components/common/labelled-input'
import {type ColumnDef} from '@tanstack/react-table'

import {
  NumberFilter,
  CountryFilter,
  YesNoFilter
} from '@frontrow/console/src/components/common/data-table/filters'
import DataTable, {
  type FilterT,
  type FetchDataParamsT,
  type DataTableColumnMeta
} from '@frontrow/console/src/components/common/data-table'
import {SchoolsDeleteButtonModal} from '@frontrow/console/src/components/pages/schools-page/schools-data-table/schools-delete-button-modal'
import {type DetailedSchoolT} from '@frontrow/console/src/models/school'
import {Routes} from '@frontrow/console/src/routers/routes'
import {bulkSelectSchoolsInputWrapper, bulkConfigureLink} from './schools-data-table.module.scss'
import Restricted from '@frontrow/console/src/components/common/restricted'

type Props = {
  defaultFiltered: Array<FilterT>
  schoolData: Array<DetailedSchoolT>
  fetchSchools: (fetchDataParams: FetchDataParamsT) => void
  isLoading: boolean
  error: string | null
}

type IndeterminateCheckboxStateT = 'selected' | 'unselected' | 'indeterminate'

export function SchoolsDataTable(props: Props): React.ReactElement {
  const {fetchSchools, schoolData, defaultFiltered, isLoading, error} = props

  const selectAllCheckboxRef = React.useRef<HTMLInputElement>(null)

  const [selections, setSelections] = React.useState<Array<number>>([])
  const [selectionState, setSelectionState] =
    React.useState<IndeterminateCheckboxStateT>('unselected')

  function toggleSelectionForSchool(schoolId: number) {
    setSelections(prevSelections => toggleMembership(schoolId, prevSelections))
  }

  function toggleSelectAll() {
    switch (selectionState) {
      case 'selected':
        setSelections([])
        break
      case 'unselected':
      case 'indeterminate':
        setSelections(map(schoolData, school => school.id))
        break
      default:
        exhaustive(selectionState, `IndeterminateCheckboxStateT`)
    }
  }

  const numSelections = selections.length
  const numSchools = schoolData.length

  React.useEffect(() => {
    if (numSelections === numSchools) {
      setSelectionState('selected')
    } else if (numSelections > 0) {
      setSelectionState('indeterminate')
    } else {
      setSelectionState('unselected')
    }
  }, [numSelections, numSchools])

  React.useEffect(() => {
    // anytime the number of schools change,
    // clear selections
    setSelections([])
  }, [numSchools])

  React.useEffect(() => {
    if (selectAllCheckboxRef.current !== null && selectAllCheckboxRef.current !== undefined) {
      selectAllCheckboxRef.current.indeterminate = selectionState === 'indeterminate'
    }
  }, [selectionState])

  const columns: ColumnDef<DetailedSchoolT, unknown>[] = [
    {
      id: 'school.select',
      header: '',
      accessorFn: school => school.id,
      maxSize: 60,
      enableColumnFilter: false,
      enableSorting: false,
      cell: ctx => (
        <div role="button" onClick={() => toggleSelectionForSchool(ctx.row.original.id)}>
          <div className="flex items-center justify-center">
            <input type="checkbox" checked={selections.includes(ctx.row.original.id)} readOnly />
          </div>
        </div>
      )
    },
    {
      id: 'school.id',
      header: 'School Id',
      accessorFn: school => school.id,
      meta: {Filter: NumberFilter} satisfies DataTableColumnMeta
    },
    {
      id: 'school.sisId',
      header: 'SIS Id',
      accessorFn: school => school.sisId
    },
    {
      id: 'school.name',
      header: 'Name',
      accessorFn: school => school.name
    },
    {
      id: 'school.city',
      header: 'City',
      accessorFn: school => school.city
    },
    {
      id: 'school.administrativeArea',
      header: 'Administrative Area',
      accessorFn: school => school.administrativeArea,
      maxSize: 90
    },
    {
      id: 'school.postalCode',
      header: 'Postal Code',
      accessorFn: school => school.postalCode,
      maxSize: 90
    },
    {
      id: 'school.countryCode',
      header: 'Country',
      accessorFn: school =>
        mmap(cc => mmap(c => c.country, getCountryByIso3(cc)), school.countryCode),
      meta: {Filter: CountryFilter} satisfies DataTableColumnMeta
    },
    {
      id: 'school.active',
      header: 'Active',
      accessorFn: school => (school.active ? 'Yes' : 'No'),
      meta: {Filter: YesNoFilter} satisfies DataTableColumnMeta,
      maxSize: 60
    },
    {
      id: 'school.crmId',
      header: 'CRM Id',
      accessorFn: school => school.crmId
    },
    {
      id: 'school.crmAccountNumber',
      header: 'CRM Account Number',
      accessorFn: school => school.crmAccountNumber,
      meta: {Filter: NumberFilter} satisfies DataTableColumnMeta
    },
    {
      id: 'district.id',
      header: 'District Id',
      accessorFn: school => school.districtId,
      maxSize: 60,
      meta: {Filter: NumberFilter} satisfies DataTableColumnMeta
    },
    {
      id: 'district.name',
      header: 'District Name',
      accessorFn: school => school.districtName
    },
    {
      id: 'school.renaissanceSchoolClient.clientId',
      header: 'RL Client Id',
      accessorFn: school => mmap(client => client.clientId, school.renaissanceSchoolClient)
    },
    {
      id: 'action',
      header: 'Action',
      accessorFn: () => null,
      cell: ctx => {
        const school = ctx.row.original
        return (
          <div className="space-x-1">
            <Link
              title="Update School"
              href={Routes.schools.schoolEdit(school.id)}
              state={{filters: defaultFiltered}}
              reactRouter
            >
              <FontAwesomeIcon icon={faPencilAlt} size="lg" />
            </Link>

            <SchoolsDeleteButtonModal schoolToDelete={school} />

            <Link
              title="Update School Licenses"
              href={Routes.schools.schoolLicenses(school.id)}
              state={{filters: defaultFiltered}}
              reactRouter
            >
              <FontAwesomeIcon icon={faList} size="lg" />
            </Link>

            {school.renaissanceRpIdentifier !== null ? (
              <Restricted to="admin">
                <Link
                  title="Download RGP CSVs"
                  href={Routes.districts.rgpCsvs(school.districtId, school.id)}
                  state={{filters: defaultFiltered}}
                  reactRouter
                >
                  <FontAwesomeIcon icon={faFileCsv} size="lg" />
                </Link>
              </Restricted>
            ) : null}
          </div>
        )
      },
      enableColumnFilter: false,
      enableSorting: false
    }
  ]

  const errorNode = error !== null && error !== undefined ? <ErrorAlert>{error}</ErrorAlert> : null

  return (
    <React.Fragment>
      {errorNode}
      <div className={bulkSelectSchoolsInputWrapper}>
        <LabelledInput label={`Select ${numSchools} schools`}>
          <input
            type="checkbox"
            // eslint-disable-next-line react/jsx-no-bind
            onChange={toggleSelectAll}
            checked={selectionState === 'selected'}
            ref={selectAllCheckboxRef}
          />
        </LabelledInput>
        {numSelections > 1 ? (
          <Link
            addClass={bulkConfigureLink}
            reactRouter
            href={Routes.schools.bulkEditSchoolConfiguration(
              mkNonEmptyFromJust(selections),
              defaultFiltered
            )}
          >
            Manage Renaissance configuration
          </Link>
        ) : null}
      </div>
      <DataTable
        columns={columns}
        fetchData={fetchSchools}
        data={schoolData}
        isLoading={isLoading}
        filterable
        manualFiltering
        updateUrlOnFilter
        defaultFiltered={defaultFiltered}
        defaultPageSize={100}
      />
    </React.Fragment>
  )
}
